// src/App.tsx
import React, { useEffect, useRef, useState } from "react";
import Header from "./Header";
import Footer from "./Footer";
import { initGA, logPageView } from './analytics';


/* -------------------------------------------------------
   COMPOSANT D'ANIMATION AU SCROLL
-------------------------------------------------------- */
function FadeInOnScroll({
  children,
  delay = 0,
  className = "",
  sharedVisibility = null,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  sharedVisibility?: boolean | null;
}) {
  const ref = useRef<HTMLParagraphElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (sharedVisibility !== null) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, [sharedVisibility]);

  const visible = sharedVisibility !== null ? sharedVisibility : isVisible;

  return (
    <p
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`
        transition-all duration-700 ease-out 
        ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}
        ${className}
      `}
    >
      {children}
    </p>
  );
}

/* -------------------------------------------------------
   APPLICATION
-------------------------------------------------------- */

function App() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });
    useEffect(() => {
    initGA();
    logPageView();
  }, []);

  const [isDevisOpen, setIsDevisOpen] = useState(false);

  const [citationVisible, setCitationVisible] = useState(false);
  const citationRef = useRef<HTMLDivElement | null>(null);
  
  const [selectedService, setSelectedService] = useState<number | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Projets du portfolio
  const projects = [
    { title: "Logo LX Proto3D", 
      description: "Modélisation et production des dessins techniques, puis impression 3D du logo de LX Proto3D à partir du visuel 2D initial.", 
      image: "/Logo_Objet.jpg"
    },
    { title: "Bee Tunnel", 
      description: "Redesign et ajout de fonctionnalités puis production des dessins techniques, suivi de l'impression 3D. L'objet permet notamment aux abeilles de rentrer dans la ruche en toute sécurité.", 
      image: "/Bee_Tunnel_Objet_recadre.png"
    },
    { title: "Honeycomb Frame Holder", 
      description: "Modéliation d'après un cahier des charges, d'un support adaptable pour cadres de ruches, pour que le client puisse l'imprimer lui-même.", 
      image: "/HFH_3D_ens.png"
    },
    { title: "Bee Escape", 
      description: "Redesign d'après les cotes demandées, production des dessins techniques, puis impression 3D d'un dispositif permettant de faire sortir les abeilles d'une ruche avant de récupérer le miel sans les blesser.", 
      image: "/BeeEscape_Objet1.jpg"
    },
    { title: "Pièce quelconque", 
      description: "Modélisation d'après une photo et des cotes, pour que le client puisse l'imprimer lui-même.", 
      image: "/CCH_3D.png"
    },
  ];

  const handlePrevProject = () => {
    setCurrentProjectIndex((prev) => {
      if (prev === 0) {
        return projects.length - 1;
      }
      return prev - 1;
    });
  };

  const handleNextProject = () => {
    setCurrentProjectIndex((prev) => {
      if (prev >= projects.length - 1) {
        return 0;
      }
      return prev + 1;
    });
  };

  // Fonction pour fermer le modal avec animation
  const handleCloseModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setSelectedService(null);
      setIsClosing(false);
    }, 300);
  };

  // Observer pour la zone de citation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCitationVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (citationRef.current) observer.observe(citationRef.current);

    return () => observer.disconnect();
  }, []);

  // Gestion du scroll fluide vers les sections
  useEffect(() => {
    const handleSmoothScroll = (e: Event) => {
      const target = e.target as HTMLAnchorElement;
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const id = target.getAttribute('href')?.substring(1);
        const element = document.getElementById(id || '');
        if (element) {
          let offset = 0;
          if (id === 'contact') {
            offset = 60;
          } else if (id === 'about') {
            offset = 30;
          } else if (id === 'services') {
            offset = 50;
          } else if (id === 'portfolio') {
            offset = 50;
          }
          
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - offset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    };

    document.addEventListener('click', handleSmoothScroll);
    return () => document.removeEventListener('click', handleSmoothScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const response = await fetch("https://formspree.io/f/mzznzwwa", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          message: formData.message
        }),
      });

      if (response.ok) {
        alert("Merci pour votre message ! Nous vous répondrons bientôt.");
        setFormData({ firstName: "", lastName: "", email: "", message: "" });
      } else {
        alert("Erreur lors de l'envoi, réessayez.");
      }
    } catch (error) {
      console.error(error);
      alert("Erreur réseau. Réessayez plus tard.");
    }
  };

  // Données des services
  const services = [
    {
      title: "Impression 3D",
      description: (
        <>
          Impression 3D rapide et précise à partir de vos fichiers <span className="font-bold" style={{ color: "#FFFFFF" }}>STL</span> ou <span className="font-bold" style={{ color: "#FFFFFF" }}>STEP</span>, réalisée sur Bambu Lab P2S (256 mm³ disponibles). <br />
          Idéel pour prototypes, pièces fonctionnelles ou remplacements. <br />
          Qualité soignée, délais courts, matériaux variés.<br /><br />
          Tarif indicatif : À partir de 0,15 €/g (calcul selon la complexité et le matériau).
        </>
      ),
      image: "/Logo_Objet.jpg",
      imageAlt: "Impression 3D - LX Proto3D"
    },
    {
      title: "Conception/Modélisation 3D",
      description: (
        <>
          Conception et modélisation 3D à partir de plans, croquis ou photos. Création de fichiers STL prêts pour l'impression ou la production, et de plans 2D en fichiers PDF si besoin.<br />
          Idéal pour prototypes, pièces sur mesure ou améliorations techniques.<br />
          Précision, adaptabilité et respect de vos contraintes techniques.
        </>
      ),
      image: "/Logo_3D.png",
      imageAlt: "Modélisation 3D - LX Proto3D"
    },
    {
      title: "Du plan à la réalité",
      description: (
        <>
          Nous transformons vos idées, croquis ou photos en pièces tangibles grâce à la modélisation 3D puis à l'impression sur Bambu Lab P2S. <br />
          Prototypes, pièces sur mesure ou petites séries, tout est possible. <br />
          Précision, rapidité et qualité à chaque étape, du concept à la pièce finale.
        </>
      ),
      image: "/Logo_dpalr.png",
      imageAlt: "Du plan à la réalité - LX Proto3D"
    }
  ];

  return (
    <div
      className="min-h-screen text-white"
      style={{
        background:
          "linear-gradient(135deg, #3a3a3a 0%, #2d2d2d 25%, #4a4a4a 50%, #353535 75%, #3a3a3a 100%)",
        backgroundAttachment: "fixed",
      }}
    >
      <Header />

      <main className="pt-20">

        {/* SECTION HERO ------------------------------------------------- */}
        <section
          id="hero"
          className="relative w-full h-[60vh] md:h-[92vh] flex items-end justify-center pt-20"
          style={{
            backgroundImage: "url('/accueil.JPG')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <style>
            {`
              @keyframes fadeInUp {
                from { opacity: 0; transform: translateY(40px); }
                to   { opacity: 1; transform: translateY(0); }
              }
              .hero-title {
                animation: fadeInUp 1.5s ease-out 0.5s forwards;
                opacity: 0;
              }
            `}
          </style>

          <h1
            className="hero-title text-3xl md:text-6xl lg:text-8xl font-serif text-center px-4 mb-6 md:mb-11"
            style={{
              color: "#FFFFFF",
              textShadow: "0 4px 20px rgba(0, 0, 0, 0.8)",
            }}
          >
            Votre Projet, notre Expertise : <br />
            du Plan à la Pièce Imprimée
          </h1>
        </section>

        {/* SECTION A PROPOS ---------------------------------------------- */}
        <section
          id="about"
          className="scroll-offset py-12 md:py-24 pb-14 md:pb-28 text-left relative"
          style={{ width: "100%" }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(255, 255, 255, 0.03)",
              backdropFilter: "blur(10px)",
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              zIndex: 0,
            }}
          />

          <div className="px-4 md:ml-8 md:px-8" style={{ position: "relative", zIndex: 1 }}>
            <h1
              className="text-4xl md:text-6xl font-serif mb-6 md:mb-10"
              style={{
                fontFamily: "Libre Baskerville, serif",
                color: "#FFFFFF",
              }}
            >
              Bienvenue chez LX Proto3D
            </h1>

            <p
              className="text-lg md:text-xl font-sans font-bold mb-6 md:mb-8"
              style={{
                fontFamily: "Almarai, sans-serif",
                color: "#FFFFFF",
              }}
            >
              Conception et impression 3D pour professionnels et particuliers
            </p>

            <p
              className="text-base md:text-lg font-sans leading-7 md:leading-8"
              style={{
                fontFamily: "Almarai, sans-serif",
                color: "#e0e0e0",
              }}
            >
              Chez <span className="font-bold" style={{ color: "#FFFFFF" }}>LX Proto3D</span>, nous donnons vie à vos idées, qu'elles soient techniques, industrielles ou personnelles. <br />
              Grâce à une maîtrise complète de la <span className="font-bold" style={{ color: "#FFFFFF" }}> modélisation 3D / CAO </span>, et à une <span className="font-bold" style={{ color: "#FFFFFF" }}>impression 3D de haute qualité</span> réalisée sur Bambu Lab P2S, nous réalisons vos pièces avec <br className="hidden md:block" />
              précision, réactivité et fiabilité.
            </p>

            <p
              className="text-base md:text-lg font-sans leading-7 md:leading-8 mb-4 md:mb-6 mt-4"
              style={{
                fontFamily: "Almarai, sans-serif",
                color: "#e0e0e0",
              }}
            >
              Que vous soyez <span className="font-bold" style={{ color: "#FFFFFF" }}>particulier, artisan, étudiant, une entreprise, un bureau d'études</span> ou <span className="font-bold" style={{ color: "#FFFFFF" }}>une startup</span>, nous vous accompagnons dans toutes les étapes :
            </p>

            <ul
              className="list-disc pl-6 text-base md:text-lg font-sans leading-7 md:leading-8 mb-4 md:mb-6"
              style={{
                fontFamily: "Almarai, sans-serif",
                color: "#e0e0e0",
              }}
            >
              <li>Création ou optimisation de modèles 3D,</li>
              <li>Impression à partir de fichiers STL,</li>
              <li>Prototypage fonctionnel,</li>
              <li>Reproduction ou amélioration de pièces existantes,</li>
              <li>Petites séries ou pièces uniques.</li>
            </ul>

            <p
              className="text-base md:text-lg font-sans leading-7 md:leading-8 mb-4 md:mb-6"
              style={{
                fontFamily: "Almarai, sans-serif",
                color: "#e0e0e0",
              }}
            >
              L'impression 3D, ou <span className="font-bold" style={{ color: "#FFFFFF" }}>fabrication additive</span>, consiste à construire une pièce couche par couche à partir d'un fichier numérique. Contrairement à l'usinage traditionnel qui retire de la matière, ce procédé ne dépose que ce qui est nécessaire, ce qui permet de réaliser des formes complexes avec une grande précision et un minimum de matière perdue. Chez LX Proto3D, nous utilisons la technologie <span className="font-bold" style={{ color: "#FFFFFF" }}>FDM</span> (dépôt de fil fondu) : un filament de matière plastique est chauffé puis déposé avec précision pour construire votre pièce, couche après couche, jusqu'au résultat final.
            </p>

            <p
              className="text-base md:text-lg font-sans leading-7 md:leading-8"
              style={{
                fontFamily: "Almarai, sans-serif",
                color: "#e0e0e0",
              }}
            >
              Nous pouvons travailler à partir de <span className="font-bold" style={{ color: "#FFFFFF" }}>plans cotés, croquis, photos</span>, ou même simplement d'une idée que vous souhaitez concrétiser. Si vous ne disposez pas de plans <br className="hidden md:block" />
              techiniques, nous pouvons également réaliser des plans 2D conformes à partir de vos photos, croquis ou pièces physiques. <br />
              Notre objectif, vous fournir un service <span className="font-bold"  style={{ color: "#FFFFFF" }}>simple, rapide, professionnel</span> et parfaitement adapté à vos besoins.
            </p>
            <p
              className="text-base md:text-lg font-sans leading-6 md:leading-7 mb-4 mt-4 md:mt-5"
              style={{
                fontFamily: "Almarai, sans-serif",
                color: "#e0e0e0",
              }}
            >
              Explorez nos services, consultez nos réalisations et n'hésitez pas à demander un <span className="font-bold" style={{ color: "#FFFFFF" }}>devis gratuit</span> directement depuis le site. <br /> 
              Votre projet mérite une solution sur mesure – <span className="font-bold" style={{ color: "#FFFFFF" }}>Nous sommes là pour le réaliser</span>.
            </p>

            {/* Citation avec animation */}
            <FadeInOnScroll
              delay={50}
              className="text-base md:text-lg font-sans leading-6 md:leading-7 text-center mt-6 italic text-[#e0e0e0] font-[Almarai]"
            >
              « La simplicité est la sophistication ultime. Dans chaque forme
              que nous imprimons, dans chaque détail que nous respectons,
              réside le mariage parfait entre technique et beauté, entre
              complexité maîtrisée et clarté d'usage. »
            </FadeInOnScroll>

            <FadeInOnScroll
              delay={250}
              className="text-sm md:text-base font-sans text-center mt-2 text-[#b0b0b0] font-[Almarai]"
            >
              Leonardo Da Vinci
            </FadeInOnScroll>
          </div>
        </section>

        {/* SERVICES */}
        <section
          id="services"
          className="scroll-offset w-full py-12 md:py-20 pt-12 md:pt-24"
          style={{
            background: "rgba(255, 255, 255, 0.05)",
            backdropFilter: "blur(10px)",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <div className="px-4 md:ml-8 md:px-8">
            <h2 className="text-4xl md:text-6xl font-serif mb-10 md:mb-16" style={{ color: "#FFFFFF", fontFamily: "Libre Baskerville, serif" }}>
              Nos Services
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 md:mr-8">
              {services.map((service, index) => (
                <div 
                  key={index}
                  onClick={() => setSelectedService(index)}
                  className="flex flex-col bg-white/5 rounded-2xl p-5 md:p-6 border border-white/10 hover:bg-white/10 transition-all duration-300 cursor-pointer"
                >
                  <h3 className="text-2xl md:text-3xl font-serif mb-3 md:mb-4" style={{ color: "#FFFFFF", fontFamily: "Libre Baskerville, serif" }}>
                    {service.title}
                  </h3>
                  <p className="text-sm md:text-base font-sans leading-6 md:leading-7 mb-4 md:mb-6 flex-grow" style={{ color: "#e0e0e0", fontFamily: "Almarai, sans-serif" }}>
                    {service.description}
                  </p>
                  <div className="w-full h-48 md:h-60 rounded-xl mb-4 md:mb-6 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.imageAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDevisOpen(true);
                    }}
                    className="w-full py-3 rounded-full bg-white/20 hover:bg-white/30 border border-white/30 text-white font-sans transition-all duration-300" 
                    style={{ fontFamily: "Almarai, sans-serif" }}
                  >
                    Demander un devis gratuit
                  </button>
                </div>
              ))}
            </div>

            {/* Modal de devis */}
            {isDevisOpen && (
              <div 
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                style={{
                  background: "rgba(0, 0, 0, 0.9)",
                  backdropFilter: "blur(20px)",
                  animation: "fadeIn 0.3s ease-out",
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0
                }}
                onClick={() => setIsDevisOpen(false)}
              >
                <div 
                  className="flex flex-col gap-6 max-w-md w-full rounded-2xl p-6 md:p-8 border border-white/30 bg-gray-800"
                  style={{
                    animation: "slideIn 0.4s ease-out"
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2 className="text-xl md:text-2xl font-bold" style={{ color: "#FFFFFF", fontFamily: "Almarai, sans-serif" }}>
                    Formulaire bientôt disponible !
                  </h2>
                  <p className="text-base md:text-lg" style={{ color: "#e0e0e0", fontFamily: "Almarai, sans-serif" }}>
                    Pour mieux comprendre votre projet, nous travaillons actuellement sur le formulaire que nous allons vous proposer.  
                    En attendant, n'hésitez pas à nous contacter directement par <strong>email</strong> ou <strong>téléphone</strong>. Nous vous répondrons rapidement !
                  </p>
                  <button 
                    className="w-full py-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-sans transition-all"
                    style={{ fontFamily: "Almarai, sans-serif" }}
                    onClick={() => setIsDevisOpen(false)}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            )}

            {/* Modal de zoom */}
            {selectedService !== null && (
              <div 
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                style={{
                  background: "rgba(0, 0, 0, 0.9)",
                  backdropFilter: "blur(20px)",
                  animation: isClosing ? "fadeOut 0.3s ease-out" : "fadeIn 0.3s ease-out",
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0
                }}
                onClick={handleCloseModal}
              >
                <style>
                  {`
                    @keyframes fadeIn {
                      from { opacity: 0; }
                      to { opacity: 1; }
                    }
                    @keyframes fadeOut {
                      from { opacity: 1; }
                      to { opacity: 0; }
                    }
                    @keyframes slideIn {
                      from { opacity: 0; transform: scale(0.9); }
                      to { opacity: 1; transform: scale(1); }
                    }
                    @keyframes slideOut {
                      from { opacity: 1; transform: scale(1); }
                      to { opacity: 0; transform: scale(0.9); }
                    }
                  `}
                </style>
                <div 
                  className="flex flex-col md:flex-row gap-6 md:gap-10 max-w-7xl w-full max-h-[85vh] rounded-2xl p-6 md:p-10 border border-white/30 overflow-y-auto"
                  style={{
                    animation: isClosing ? "slideOut 0.3s ease-out" : "slideIn 0.4s ease-out",
                    background: "rgba(40, 40, 40, 0.98)"
                  }}
                  onClick={(e) => { 
                    e.stopPropagation();
                    setIsDevisOpen(true);
                  }}
                >
                  <div className="flex-[1.4]">
                    <div className="w-full h-64 md:h-full rounded-xl overflow-hidden bg-black/20">
                      <img
                        src={services[selectedService].image}
                        alt={services[selectedService].imageAlt}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col">
                    <h3 className="text-3xl md:text-5xl font-serif mb-6 md:mb-8" style={{ color: "#FFFFFF", fontFamily: "Libre Baskerville, serif" }}>
                      {services[selectedService].title}
                    </h3>
                    <p className="text-base md:text-xl font-sans leading-7 md:leading-9 mb-6 md:mb-10 flex-grow" style={{ color: "#e0e0e0", fontFamily: "Almarai, sans-serif" }}>
                      {services[selectedService].description}
                    </p>
                    <button 
                      className="w-full py-3 md:py-4 rounded-full bg-white/20 hover:bg-white/30 border border-white/30 text-white font-sans transition-all duration-300 text-base md:text-lg" 
                      style={{ fontFamily: "Almarai, sans-serif" }}
                    >
                      Demander un devis gratuit
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* NOS PRODUITS NFC */}
        <section
          id="products"
          className="scroll-offset w-full py-12 md:py-20 pt-12 md:pt-24"
          style={{
            background: "rgba(255, 255, 255, 0.03)",
            backdropFilter: "blur(10px)",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <style>{`
            .product-card {
              position: relative;
              overflow: hidden;
              cursor: pointer;
              transition: transform 0.3s ease, box-shadow 0.3s ease;
            }
            .product-card:hover {
              transform: translateY(-6px);
              box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
            }
            .product-card::after {
              content: '';
              position: absolute;
              inset: 0;
              background: linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 60%);
              pointer-events: none;
              border-radius: inherit;
            }
            .product-image-wrapper {
              position: relative;
              overflow: hidden;
            }
            .product-image-wrapper img {
              transition: transform 0.5s ease;
            }
            .product-card:hover .product-image-wrapper img {
              transform: scale(1.04);
            }
            .nfc-badge {
              position: absolute;
              top: 14px;
              right: 14px;
              background: rgba(255,255,255,0.15);
              backdrop-filter: blur(8px);
              border: 1px solid rgba(255,255,255,0.3);
              border-radius: 20px;
              padding: 4px 14px;
              font-size: 0.75rem;
              font-family: Almarai, sans-serif;
              letter-spacing: 0.05em;
              color: #fff;
            }
            .pdf-cta {
              display: flex;
              align-items: center;
              gap: 8px;
              margin-top: 20px;
              padding: 12px 24px;
              border-radius: 9999px;
              background: rgba(255,255,255,0.12);
              border: 1px solid rgba(255,255,255,0.25);
              color: #fff;
              font-family: Almarai, sans-serif;
              font-size: 0.95rem;
              width: fit-content;
              transition: background 0.25s ease, border-color 0.25s ease;
            }
            .product-card:hover .pdf-cta {
              background: rgba(255,255,255,0.22);
              border-color: rgba(255,255,255,0.45);
            }
            .pdf-icon {
              width: 18px;
              height: 18px;
              opacity: 0.85;
            }
          `}</style>

          <div className="px-4 md:ml-8 md:px-8">
            {/* En-tête */}
            <div className="flex flex-wrap items-end gap-4 mb-4">
              <h2 className="text-4xl md:text-6xl font-serif" style={{ color: "#FFFFFF", fontFamily: "Libre Baskerville, serif" }}>
                Nos Produits
              </h2>
              <span
                className="mb-1 px-4 py-1 rounded-full text-sm font-sans"
                style={{ fontFamily: "Almarai, sans-serif", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.2)", color: "#c8c8c8", letterSpacing: "0.1em" }}
              >
                ✦ NFC intégré
              </span>
            </div>

            <p
              className="text-base md:text-lg font-sans leading-7 md:leading-8 mb-10 md:mb-14"
              style={{ fontFamily: "Almarai, sans-serif", color: "#c0c0c0" }}
            >
              Nos produits connectés combinent impression 3D et technologie NFC pour créer des objets utiles et modernes. Simple pour vos clients, puissant pour votre communication.<br />Cliquez sur un produit pour consulter la grille tarifaire.
            </p>

            {/* Grille deux colonnes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 md:mr-8">
              {[
                { title: "Porte-clés NFC", description: "Nos porte-clés personnalisables en couleur et en forme intègrent la technologie NFC pour offrir de nouvelles perspectives marketing à votre entreprise. Un simple contact avec un smartphone suffit pour ouvrir instantanément la page de votre choix — site web, avis Google, portfolio, réseaux sociaux. Une carte de visite connectée, élégante et qui ne quitte jamais votre client.", image: "/portecles.jpg", imageAlt: "Porte-clés NFC - LX Proto3D", pdfPath: "/Grille_Tarifaire_Portecles.pdf", badge: "Porte-clés" },
                { title: "Présentoirs NFC", description: "Nos présentoirs personnalisables intègrent la technologie NFC pour transformer chaque point de contact avec vos clients en expérience connectée. Posés sur un comptoir, une table ou en vitrine, un simple effleurement de smartphone suffit pour accéder à votre menu, vos avis Google, votre site ou vos réseaux sociaux — chaque présentoir pouvant embarquer plusieurs puces NFC pour multiplier les points d'accès.", image: "/presentoir.jpg", imageAlt: "Présentoirs NFC - LX Proto3D", pdfPath: "/Grille_Tarifaire_Presentoirs.pdf", badge: "Présentoirs" }
              ].map((product, index) => (
                <div
                  key={index}
                  className="product-card bg-white/5 rounded-2xl border border-white/10"
                  onClick={() => window.open(product.pdfPath, '_blank')}
                >
                  {/* Image */}
                  <div className="product-image-wrapper w-full h-64 md:h-80 rounded-t-2xl bg-black/20 relative">
                    <img
                      src={product.image}
                      alt={product.imageAlt}
                      className="w-full h-full object-contain rounded-t-2xl"
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent && !parent.querySelector('.placeholder-msg')) {
                          const placeholder = document.createElement('div');
                          placeholder.className = 'placeholder-msg';
                          placeholder.style.cssText = 'width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,0.25);font-family:Almarai,sans-serif;font-size:0.95rem;letter-spacing:0.05em;';
                          placeholder.textContent = 'Image à venir';
                          parent.appendChild(placeholder);
                        }
                      }}
                    />
                    <span className="nfc-badge">{product.badge}</span>
                    {/* Overlay hover */}
                    <div
                      className="absolute inset-0 rounded-t-2xl flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300"
                      style={{ background: "rgba(0,0,0,0.45)" }}
                    >
                      <div className="flex flex-col items-center gap-3">
                        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                          <line x1="9" y1="13" x2="15" y2="13"/>
                          <line x1="9" y1="17" x2="13" y2="17"/>
                        </svg>
                        <span style={{ color: "#fff", fontFamily: "Almarai, sans-serif", fontSize: "0.9rem", letterSpacing: "0.05em" }}>
                          Voir la grille tarifaire
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contenu texte */}
                  <div className="p-5 md:p-7">
                    <h3 className="text-2xl md:text-3xl font-serif mb-3" style={{ color: "#FFFFFF", fontFamily: "Libre Baskerville, serif" }}>
                      {product.title}
                    </h3>
                    <p className="text-sm md:text-base font-sans leading-6 md:leading-7" style={{ fontFamily: "Almarai, sans-serif", color: "#d0d0d0" }}>
                      {product.description}
                    </p>
                    {/* CTA PDF */}
                    <div className="pdf-cta">
                      <svg className="pdf-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="9" y1="13" x2="15" y2="13"/>
                        <line x1="9" y1="17" x2="13" y2="17"/>
                      </svg>
                      Consulter la grille tarifaire
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: "2px", opacity: 0.7 }}>
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/>
                        <line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PORTFOLIO */}
        <section
          id="portfolio"
          className="scroll-offset w-full py-12 md:py-20"
          style={{
            background: "rgba(255, 255, 255, 0.05)",
            backdropFilter: "blur(10px)",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <div className="px-4 md:ml-8 md:px-8">
            <h2 className="text-4xl md:text-6xl font-serif mb-6 md:mb-10" style={{ color: "#FFFFFF", fontFamily: "Libre Baskerville, serif" }}>
              Portfolio
            </h2>
            <p
              className="text-base md:text-xl font-sans mb-6"
              style={{
                fontFamily: "Almarai, sans-serif",
                color: "#FFFFFF",
              }}
            >
              Voici quelques-unes de nos réalisations récentes en modélisation et impression 3D qui vous donneront un aperçu de notre savoir-faire et de la qualité de notre travail.
            </p>

            {/* Carrousel */}
            <div className="relative md:mr-8">
              {/* Version Mobile: Flèches toujours visibles */}
              <div className="md:hidden flex items-center gap-4">
                <button 
                  onClick={handlePrevProject}
                  className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/20 hover:bg-white/30 border border-white/30 flex items-center justify-center transition-all duration-300"
                >
                  <span className="text-white text-2xl">‹</span>
                </button>

                <div className="flex-1 bg-white/5 rounded-2xl p-4 border border-white/10">
                  <div className="w-full h-48 rounded-xl mb-3 overflow-hidden">
                    <img
                      src={projects[currentProjectIndex].image}
                      alt={projects[currentProjectIndex].title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-xl font-serif mb-2" style={{ color: "#FFFFFF", fontFamily: "Libre Baskerville, serif" }}>
                    {projects[currentProjectIndex].title}
                  </h3>
                  <p className="text-sm font-sans" style={{ color: "#e0e0e0", fontFamily: "Almarai, sans-serif" }}>
                    {projects[currentProjectIndex].description}
                  </p>
                </div>

                <button 
                  onClick={handleNextProject}
                  className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/20 hover:bg-white/30 border border-white/30 flex items-center justify-center transition-all duration-300"
                >
                  <span className="text-white text-2xl">›</span>
                </button>
              </div>

              {/* Version Desktop: Hover arrows */}
              <div className="hidden md:block">
                <div 
                  className="absolute top-0 left-0 z-10"
                  style={{ 
                    width: '80px',
                    height: 'calc(256px + 120px)'
                  }}
                  onMouseEnter={() => setShowLeftArrow(true)}
                  onMouseLeave={() => setShowLeftArrow(false)}
                >
                  <button 
                    onClick={handlePrevProject}
                    className={`absolute left-0 top-1/2 -translate-y-1/2 w-12 h-16 rounded-lg bg-white/20 hover:bg-white/30 border border-white/30 flex items-center justify-center transition-all duration-300 ${
                      showLeftArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                  >
                    <span className="text-white text-3xl leading-none" style={{ marginTop: "-2px" }}>‹</span>
                  </button>
                </div>

                <div className="overflow-hidden">
                  <div 
                    className="flex gap-8 transition-transform duration-500 ease-out"
                    style={{ 
                      transform: `translateX(calc(-${currentProjectIndex} * (33.333% + 32px / 3)))` 
                    }}
                  >
                    {projects.map((project, index) => (
                      <div key={index} className="flex-shrink-0 bg-white/5 rounded-2xl p-6 border border-white/10" style={{ width: 'calc(33.333% - 21.33px)' }}>
                        <div className="w-full h-64 rounded-xl mb-4 overflow-hidden">
                          <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <h3 className="text-2xl font-serif mb-2" style={{ color: "#FFFFFF", fontFamily: "Libre Baskerville, serif" }}>
                          {project.title}
                        </h3>
                        <p className="text-sm font-sans" style={{ color: "#e0e0e0", fontFamily: "Almarai, sans-serif" }}>
                          {project.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div 
                  className="absolute top-0 right-0 z-10"
                  style={{ 
                    width: '80px',
                    height: 'calc(256px + 120px)'
                  }}
                  onMouseEnter={() => setShowRightArrow(true)}
                  onMouseLeave={() => setShowRightArrow(false)}
                >
                  <button 
                    onClick={handleNextProject}
                    className={`absolute right-0 top-1/2 -translate-y-1/2 w-12 h-16 rounded-lg bg-white/20 hover:bg-white/30 border border-white/30 flex items-center justify-center transition-all duration-300 ${
                      showRightArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                  >
                    <span className="text-white text-3xl leading-none" style={{ marginTop: "-2px" }}>›</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Feedback Section */}
          <div className="px-4 md:ml-8 md:px-8 mt-8">
            <h2 className="text-3xl md:text-4xl font-serif mb-4 md:mb-6" style={{ color: "#FFFFFF", fontFamily: "Libre Baskerville, serif" }}>
              Feedback
            </h2>
            <div className="text-base md:text-xl font-sans mb-6" style={{ fontFamily: "Almarai, sans-serif", color: "#FFFFFF" }}>
              <p className="mb-4">« So cool, very well done ! » - Markus H</p>
              <p className="mb-4">« Très satisfait du résultat. » - Jean D</p>
              <p>« L'impression est propre et conforme aux plans fournis, je recommande. » - Marie A</p>
            </div>
          </div>
        </section>

        {/* CONTACT */}
        <section
          id="contact"
          className="scroll-offset w-full py-12 md:py-20"
          style={{
            background: "rgba(255, 255, 255, 0.05)",
            backdropFilter: "blur(10px)",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <div className="flex flex-col md:flex-row gap-8 md:gap-16 px-4 md:ml-8 md:px-8">
            <div className="flex-1 flex flex-col justify-center">
              <h2 className="text-4xl md:text-6xl font-serif mb-6 md:mb-8" style={{ color: "#FFFFFF", fontFamily: "Libre Baskerville, serif" }}>
                Nous contacter
              </h2>
              <p className="text-base md:text-xl font-sans leading-relaxed" style={{ color: "#e0e0e0", fontFamily: "Almarai, sans-serif" }}>
                Vous avez envie de travailler avec nous mais vous avez des questions ou 
                des appréhensions ? N'hésitez pas à nous contacter !
              </p>
            </div>

            <div className="flex-1 md:mr-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-sans mb-2" style={{ color: "#FFFFFF", fontFamily: "Almarai, sans-serif" }}>
                      Prénom <span style={{ color: "#ff4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      className="w-full px-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-white/40 transition-colors"
                      style={{ fontFamily: "Almarai, sans-serif" }}
                      placeholder="Votre prénom"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-sans mb-2" style={{ color: "#FFFFFF", fontFamily: "Almarai, sans-serif" }}>
                      Nom <span style={{ color: "#ff4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      className="w-full px-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-white/40 transition-colors"
                      style={{ fontFamily: "Almarai, sans-serif" }}
                      placeholder="Votre nom"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-sans mb-2" style={{ color: "#FFFFFF", fontFamily: "Almarai, sans-serif" }}>
                    E-mail <span style={{ color: "#ff4444" }}>*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-white/40 transition-colors"
                    style={{ fontFamily: "Almarai, sans-serif" }}
                    placeholder="votre.email@exemple.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-sans mb-2" style={{ color: "#FFFFFF", fontFamily: "Almarai, sans-serif" }}>
                    Message <span style={{ color: "#ff4444" }}>*</span>
                  </label>
                  <textarea
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-4 py-3 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-white/40 transition-colors resize-none"
                    style={{ fontFamily: "Almarai, sans-serif" }}
                    placeholder="Décrivez votre projet ou posez-nous vos questions..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.message}
                  className="px-8 py-3 rounded-full bg-white/20 hover:bg-white/30 border border-white/30 text-white font-sans transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white/20"
                  style={{ fontFamily: "Almarai, sans-serif" }}
                >
                  Envoyer
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default App;